import { FC, useCallback, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { ContainerMedium, ContainerSmall } from 'shared/components/Container';
import { H6, PSmall } from 'shared/components/Typography';
import { Info } from 'shared/components/Info';
import { Spacer } from 'shared/components/Layout';
import { Tooltip } from 'shared/components/Tooltip';
import { Wrapper } from 'shared/components/Wrapper';

import {
  DealerPercentageContainer,
  DealerPercentageInput,
  getAppliedPercentage,
} from './DealerPercentageInput';

interface FreightProps {
  canEdit: boolean;
  totalExcludedNetPrice: number;
  defaultFreight: number; // Default Dealer Freight Percentage
  customFreight: number | null; // Custom Dealer Freight Percentage
  onFreightChange: (price: number) => void;
  onSave?: (customFreightAmount: number | null) => void;
}

export const Freight: FC<FreightProps> = ({
  canEdit,
  totalExcludedNetPrice,
  defaultFreight,
  customFreight,
  onFreightChange,
  onSave,
}) => {
  const {
    watch,
    formState: { errors },
  } = useFormContext();

  const freight = watch('freight');

  const getFreightPrice = useCallback(() => {
    if (!errors.freight) {
      const numValue = getAppliedPercentage(freight, defaultFreight);
      if (!Number.isNaN(numValue)) {
        return (totalExcludedNetPrice * numValue) / 100;
      }
    }

    return 0;
  }, [freight, defaultFreight, totalExcludedNetPrice, errors.freight]);

  // Notify parent component when freight price changes
  useEffect(() => {
    const price = getFreightPrice();
    onFreightChange(price);
  }, [getFreightPrice, onFreightChange]);

  return (
    <DealerPercentageContainer>
      <Wrapper flex middle>
        <ContainerMedium isTableCell flex middle mrAuto>
          <H6>FREIGHT</H6>

          <Spacer w="10px" />

          <Info>
            <Tooltip position="right">
              Calculated based on NET value of excluded total.
            </Tooltip>
          </Info>
        </ContainerMedium>

        <DealerPercentageInput
          name="freight"
          canEdit={canEdit}
          defaultValue={defaultFreight}
          customValue={customFreight}
          max={99.99}
          onSave={onSave}
        />

        <ContainerSmall isTableCell>
          <PSmall>{getFreightPrice().toFixed(2)}</PSmall>
        </ContainerSmall>
      </Wrapper>
    </DealerPercentageContainer>
  );
};
